import { useLocation } from 'react-router-dom';

import { formatMoney } from '@/lib/shared-utils';
import { cn } from '@/lib/utils';

/**
 * Route prefixes where product/monetary values are allowed to render.
 * Keep in sync with the inventory product routes in src/App/App.tsx
 * (list, /create, /catalog, /:slug/detail, /:slug/edit).
 */
export const PRICE_VISIBLE_PREFIXES = ['/inventory/products'];

/**
 * Whether monetary values may be shown for the current route. Prices are hidden
 * everywhere except the inventory product pages. Pass `allowed` to override the
 * route check (e.g. inside a portal/dialog rendered from an allowed page).
 */
export function usePriceVisible(allowed?: boolean): boolean {
    const { pathname } = useLocation();
    if (allowed !== undefined) return allowed;
    return PRICE_VISIBLE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

interface PriceProps {
    value: string | number | null | undefined;
    prefix?: string;
    className?: string;
    /** Override the route-based visibility check. */
    allowed?: boolean;
    /** Rendered when the price is hidden. Defaults to nothing. */
    fallback?: React.ReactNode;
}

/**
 * Renders a formatted monetary value, but only on routes where prices are
 * allowed (inventory product pages). Everywhere else it renders `fallback`
 * (nothing by default), so prices stay hidden in the UI without deleting data.
 */
export function Price({
    value,
    prefix = 'NPR ',
    className,
    allowed,
    fallback = null,
}: PriceProps): React.ReactNode {
    const visible = usePriceVisible(allowed);
    if (!visible) return fallback;
    return (
        <span className={cn('tabular-nums', className)}>
            {prefix}
            {formatMoney(value)}
        </span>
    );
}
